'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

export type CookieConsent = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

const CONSENT_KEY = 'cookie-consent';

export function getStoredConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

function storeConsent(consent: CookieConsent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: consent }));
}

export function CookieConsentBanner() {
  const t = useTranslations('common');
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const acceptAll = () => {
    storeConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    storeConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    storeConsent({ essential: true, analytics, marketing });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 sm:right-auto sm:left-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4 duration-500"
      // Stack above the selection bar AND, when present, the own-hold reclaim
      // banner (which also anchors just above the selection bar) so the two
      // never overlap. Both vars default to 0 when their element isn't shown.
      style={{ bottom: 'calc(1rem + var(--selection-bar-height) + var(--own-hold-banner-height, 0px))' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-[#4e3529]/10 overflow-hidden">
        <div className="p-5">
          <h3 className="font-[family-name:var(--font-poppins)] font-semibold text-[#2c1810] text-sm mb-2">
            {t('cookieConsent.title')}
          </h3>
          <p className="text-xs text-[#4e3529]/65 leading-relaxed mb-4">
            {t('cookieConsent.description')}{' '}
            <Link href="/cookies" className="text-[#801515] underline hover:text-[#601010]">
              {t('cookieConsent.learnMore')}
            </Link>
          </p>

          {showCustomize && (
            <div className="space-y-3 mb-4 p-3 bg-[#faf7f4] rounded-xl">
              <label className="flex items-center gap-3 cursor-not-allowed">
                <input type="checkbox" checked disabled className="w-4 h-4 accent-[#801515] rounded" />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.essential')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.alwaysOn')}</span>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="w-4 h-4 accent-[#801515] rounded"
                />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.analytics')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.googleAnalytics')}</span>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="w-4 h-4 accent-[#801515] rounded"
                />
                <div>
                  <span className="text-xs font-medium text-[#2c1810]">{t('cookieConsent.marketing')}</span>
                  <span className="text-[10px] text-[#4e3529]/45 ml-1">{t('cookieConsent.metaPixel')}</span>
                </div>
              </label>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <button
              onClick={acceptAll}
              className="w-full py-2.5 bg-[#801515] text-white rounded-xl text-xs font-medium hover:bg-[#601010] transition-colors"
            >
              {t('cookieConsent.acceptAll')}
            </button>
            {showCustomize ? (
              <button
                onClick={saveCustom}
                className="w-full py-2.5 bg-[#faf7f4] text-[#2c1810] border border-[#4e3529]/15 rounded-xl text-xs font-medium hover:bg-[#f0e8e0] transition-colors"
              >
                {t('cookieConsent.savePreferences')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={rejectNonEssential}
                  className="flex-1 py-2.5 bg-[#faf7f4] text-[#2c1810] border border-[#4e3529]/15 rounded-xl text-xs font-medium hover:bg-[#f0e8e0] transition-colors"
                >
                  {t('cookieConsent.essentialOnly')}
                </button>
                <button
                  onClick={() => setShowCustomize(true)}
                  className="flex-1 py-2.5 bg-[#faf7f4] text-[#2c1810] border border-[#4e3529]/15 rounded-xl text-xs font-medium hover:bg-[#f0e8e0] transition-colors"
                >
                  {t('cookieConsent.customize')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
